import floor from 'lodash/floor';
import debounce from 'lodash/debounce';
import bindAll from 'lodash/bindAll';
import gsap from 'gsap';
import classes from 'dom-helpers/class';
import normalizeWheel from 'normalize-wheel';
import inViewport from 'in-viewport';
import 'gsap/SplitText.min';
import 'gsap/src/uncompressed/easing/CustomEase';
import { lerp, stringToDOM, animateShadows } from '../base/utils';
import ScrollBar from './scrollbar';

class ScrollAnim {
    constructor(view) {
        this.view = typeof view === 'string' ? { container: document.querySelector(view) } : { container: view };
        this.delta = 0;
        this.interpolated = 0;
        this.final = [];
        this.oldDelta = 'down';
        this.height = this.calculateHeight();
        this.animations = new Map();
        this.setAnimations();
        this.scrollbar = new ScrollBar('#scrollbar');

        this.view = Object.assign({}, this.view, {
            animate: Array.from(this.view.container.querySelectorAll('[data-animation]')),
            parallax: Array.from(this.view.container.querySelectorAll('[data-parallax]')),
        });

        bindAll(this, ['render', 'updateDelta', 'keyDown', 'keyUp', 'resize']);

        if (this.view.animate.length) {
            this.view.parallax.forEach(() => {
                this.final.push(0);
            });

            this.bindScroll();
            this.render();
            this.fromAnimations();
            this.animateEntrance();
            this.scrollHandler();

            window.addEventListener('resize', debounce(this.resize, 500));

            this.height = this.calculateHeight();
        }
    }

    render() {
        this.interpolated = floor(lerp(this.interpolated, this.delta, 0.07), 2);
        this.view.container.style.transform = `translate3d(0, -${this.interpolated}px, 0)`;

        this.scrollHandler();
        this.scrollbar.update(this.interpolated, this.height);

        window.requestAnimationFrame(this.render);
    }

    calculateHeight() {
        return this.view.container.clientHeight - window.innerHeight;
    }

    scrollHandler() {
        Array.from(this.view.parallax).forEach((el, i) => {
            if (inViewport(el, { offset: 900 }) && el.getAttribute('data-parallax') !== null) {
                this.parallaxin(el, i);
            }
        });
    }

    parallaxin(el, i) {
        const elH = el.parentElement.clientHeight;
        const offsetTopFromCenter = (el.parentElement.getBoundingClientRect().top - ((window.innerHeight - elH) / 2));
        let final = this.final[i];

        const coefficent = el.getAttribute('data-parallax') !== null ? el.getAttribute('data-parallax') : 1;
        const maxY = Math.max(-window.innerHeight, offsetTopFromCenter);
        const baseTransform = floor(Math.min(window.innerHeight, maxY), 2);
        const translateY = baseTransform * coefficent;

        final = lerp(final, translateY, 1);

        gsap.set(el, { y: final });
    }

    bindScroll() {
        this.mousewheelevt = /Firefox/i.test(navigator.userAgent) ? 'DOMMouseScroll' : 'mousewheel';
        document.addEventListener(this.mousewheelevt, this.updateDelta, false);
        window.addEventListener('keydown', this.keyDown);
        window.addEventListener('keyup', this.keyUp);
    }

    updateDelta(e, y) {
        let wDelta, deltaCorrect;

        if (e === 'fromKey') {
            deltaCorrect = y;
            wDelta = -deltaCorrect < 0 ? 'down' : 'up';
        } else {
            e.preventDefault();
            e.stopPropagation();
            const normalized = normalizeWheel(e);
            deltaCorrect = normalized.pixelY;
            wDelta = -deltaCorrect < 0 ? 'down' : 'up';
        }

        if (this.delta >= this.height && wDelta === 'down') {
            this.delta = this.height;
            return;
        } else if (this.delta < 0 && wDelta === 'up') {
            return;
        } else {
            this.delta += (deltaCorrect * 1.1);
        }

        this.cursorHelpers(wDelta);
        const hideEls = Array.from(document.querySelectorAll('[data-hide]'));
        hideEls.forEach((el) => {
            if (!el.getAttribute('data-animated')) {
                el.setAttribute('data-animated', true);
                gsap.to(hideEls, 0.4, {
                    autoAlpha: 0,
                });
            }
        });
    }

    animateEntrance() {
        this.view.animate.forEach((el) => {
            inViewport(el, { offset: -200 }, () => {
                const animation = el.getAttribute('data-animation');
                const isAnimated = el.getAttribute('data-animated');

                if (isAnimated === 'false') {
                    el.setAttribute('data-animated', 'true');
                    this.animations.get(animation).to(el);
                }
            });
        });
    }

    fromAnimations() {
        this.view.animate.forEach((el) => {
            const animation = el.getAttribute('data-animation');
            el.setAttribute('data-animated', 'false');
            this.animations.get(animation).from(el);
        });
    }

    resize() {
        this.height = this.calculateHeight();

        this.view.animate.forEach((el) => {
            const animation = el.getAttribute('data-animation');
            if (typeof this.animations.get(animation).update === 'function') {
                this.animations.get(animation).update(el);
            }
        });
    }

    setAnimations() {
        this.animations.set('reveal', {
            from(el) {
                const text = el.innerHTML;
                const string = `<div class="reveal-text">${text}</div>`;
                const domEl = stringToDOM(string);

                el.appendChild(domEl);
                const split = new SplitText(domEl, { type: 'lines', linesClass: 'line' }).lines;

                gsap.set(split, {
                    clipPath: 'polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%)',
                    display: 'block',
                });

                split.forEach((el) => {
                    const duplicated = el.cloneNode(true);
                    duplicated.classList = 'second-line';
                    duplicated.style.position = 'absolute';
                    el.appendChild(duplicated);
                });
            },

            update(el) {
                const reveals = el.querySelector('.reveal-text');

                if (reveals !== null && reveals !== undefined) {
                    el.removeChild(reveals);
                    this.from(el);
                }
            },

            to(el) {
                const lines = Array.from(el.querySelectorAll('.line'));
                const time = 0.9;
                const stagger = 0.2;

                lines.forEach((line, i) => {
                    const counter = { var: 0 };
                    const another = { var: 0 };
                    const duplicated = line.querySelector('.second-line');

                    gsap.to(another, time, {
                        var: 100,
                        ease: CustomEase.create('custom', 'M0,0 C0.418,0.072 0.446,0.343 0.507,0.512 0.57,0.688 0.6,0.968 1,1'),
                        delay: stagger * i,
                        onEnd: () => { another.var = 0; },
                        onUpdate: () => {
                            gsap.set(duplicated, {
                                clipPath: `polygon(${another.var}% 0%, ${another.var}% 100%, 100% 100%, 100% 0%)`,
                                webkitClipPath: `polygon(${another.var}% 0%, ${another.var}% 100%, 100% 100%, 100% 0%)`,
                            });
                        },
                    });

                    gsap.to(counter, time, {
                        var: 100,
                        ease: CustomEase.create('custom', 'M0,0 C0.418,0.072 0.446,0.343 0.507,0.512 0.57,0.688 0.6,0.968 1,1'),
                        delay: (stagger * i) + 0.5,
                        onEnd: () => {
                            counter.var = 0;
                            if (i === (lines.length - 1)) {
                                setTimeout(() => { el.removeChild(el.querySelector('.reveal-text')); }, 1000);
                            }
                        },
                        onUpdate: () => {
                            gsap.set(line, {
                                clipPath: `polygon(${counter.var}% 0%, ${counter.var}% 100%, 100% 100%, 100% 0%)`,
                                webkitClipPath: `polygon(${counter.var}% 0%, ${counter.var}% 100%, 100% 100%, 100% 0%)`,
                            });
                        },
                    });
                });
            },
        });

        this.animations.set('hide', {
            from() {},
            to(el) {
                gsap.set(el, { opacity: 0 });
            },
        });

        this.animations.set('shadow', {
            from() {},
            to(el) {
                animateShadows(el);
            },
        });

        this.animations.set('scale', {
            from() {},
            to(el) {
                classes.addClass(el, 'is-animated');
            },
        });
    }

    cursorHelpers(wDelta) {
        if (this.delta >= this.height) {
            classes.removeClass(document.body, `is-scrolling-down`);
            classes.addClass(document.body, `is-scrolling-up`);
        } else if (this.delta <= 0) {
            classes.removeClass(document.body, `is-scrolling-up`);
            classes.addClass(document.body, `is-scrolling-down`);
        } else if (this.oldDelta !== wDelta) {
            classes.removeClass(document.body, `is-scrolling-${this.oldDelta}`);
            classes.addClass(document.body, `is-scrolling-${wDelta}`);
            this.oldDelta = wDelta;
        }
    }

    keyDown(e) {
        const key = e.keyCode || e.which;

        if (key === 32 || key === 40) {
            this.updateDelta('fromKey', 400);
        }
    }

    keyUp(e) {
        const key = e.keyCode;

        if (key === 38) {
            this.updateDelta('fromKey', -400);
        }
    }
}

export default ScrollAnim;



/** WEBPACK FOOTER **
 ** ./components/scroll-anim.js
 **/